import React, { useState } from 'react';
import { FieldComponentProps } from '../../types/visual-editor';
import { Plus, Trash2, ChevronDown, ChevronRight, Type } from 'lucide-react';
import { RichTextEditor } from '../rich-text-editor';
import { ImageField } from './ImageField';
import { ImageService } from '../../services';

// Función helper para obtener valores por defecto según el tipo
const getDefaultValueForType = (type: string): any => {
  switch (type) {
    case 'string':
    case 'image':
      return '';
    case 'number':
      return 0;
    case 'boolean':
      return false;
    case 'array':
      return [];
    case 'object':
      return {};
    default:
      return '';
  }
};

export const StringField = React.memo<FieldComponentProps>(({ config, onChange, path }) => {
  const fullPath = path ? `${path}.${config.key}` : config.key;
  const [isRichTextOpen, setIsRichTextOpen] = useState(false);
  const [richTextValue, setRichTextValue] = useState(config.value || '');

  const handleRichTextSave = (value: string) => {
    setRichTextValue(value);
    onChange(fullPath, value);
    setIsRichTextOpen(false);
  };

  const handleRichTextCancel = () => {
    setIsRichTextOpen(false);
  };

  const openRichTextEditor = () => {
    setRichTextValue(config.value || '');
    setIsRichTextOpen(true);
  };

  return (
    <div className="field-group">
      <label className="field-label">
        {config.label}
        {config.description && <span className="field-description"> - {config.description}</span>}
      </label>
      <div className="text-field-container">
        <input
          type="text"
          className="input text-input"
          value={config.value || ''}
          onChange={(e) => onChange(fullPath, e.target.value)}
          placeholder={`Ingresa ${config.label.toLowerCase()}`}
        />
        <button
          type="button"
          className="btn btn-sm btn-secondary rich-text-btn"
          onClick={openRichTextEditor}
          title="Abrir editor de texto enriquecido"
        >
          <Type size={14} />
        </button>
      </div>
      
      <RichTextEditor
        initialValue={richTextValue}
        onSave={handleRichTextSave}
        onCancel={handleRichTextCancel}
        isOpen={isRichTextOpen}
      />
    </div>
  );
});

StringField.displayName = 'StringField';

export const ArrayStringItem: React.FC<{
  value: string;
  index: number;
  onChange: (value: string) => void;
  placeholder: string;
}> = ({ value, index, onChange, placeholder }) => {
  const [isRichTextOpen, setIsRichTextOpen] = useState(false);
  const [richTextValue, setRichTextValue] = useState(value || '');

  const handleRichTextSave = (newValue: string) => {
    setRichTextValue(newValue);
    onChange(newValue);
    setIsRichTextOpen(false);
  };

  const handleRichTextCancel = () => {
    setIsRichTextOpen(false);
  };

  const openRichTextEditor = () => {
    setRichTextValue(value || '');
    setIsRichTextOpen(true);
  };

  return (
    <div className="array-string-item">
      <div className="text-field-container">
        <input
          type="text"
          className="input text-input"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
        <button
          type="button"
          className="btn btn-sm btn-secondary rich-text-btn"
          onClick={openRichTextEditor}
          title="Abrir editor de texto enriquecido"
        >
          <Type size={14} />
        </button>
      </div>

      <RichTextEditor
        initialValue={richTextValue}
        onSave={handleRichTextSave}
        onCancel={handleRichTextCancel}
        isOpen={isRichTextOpen}
      />
    </div>
  );
};

export const NumberField = React.memo<FieldComponentProps>(({ config, onChange, path }) => {
  const fullPath = path ? `${path}.${config.key}` : config.key;
  
  return (
    <div className="field-group">
      <label className="field-label">
        {config.label}
        {config.description && <span className="field-description"> - {config.description}</span>}
      </label>
      <input
        type="number"
        className="input"
        value={config.value || ''}
        onChange={(e) => onChange(fullPath, parseFloat(e.target.value) || 0)}
        placeholder={`Ingresa ${config.label.toLowerCase()}`}
      />
    </div>
  );
});

NumberField.displayName = 'NumberField';

export const BooleanField = React.memo<FieldComponentProps>(({ config, onChange, path }) => {
  const fullPath = path ? `${path}.${config.key}` : config.key;
  
  return (
    <div className="field-group">
      <label className="field-label">
        {config.label}
        {config.description && <span className="field-description"> - {config.description}</span>}
      </label>
      <div className="checkbox-group">
        <input
          type="checkbox"
          id={fullPath}
          checked={config.value || false}
          onChange={(e) => onChange(fullPath, e.target.checked)}
        />
        <label htmlFor={fullPath} className="checkbox-label">
          {config.value ? 'Sí' : 'No'}
        </label>
      </div>
    </div>
  );
});

BooleanField.displayName = 'BooleanField';

// Componente memoizado para items de array de objetos
const ArrayObjectItem = React.memo<{
  item: any;
  index: number;
  config: any;
  fullPath: string;
  onChange: (path: string, value: any) => void;
  onRemove: (index: number) => void;
  imageService?: ImageService;
}>(({ item, index, config, fullPath, onChange, onRemove, imageService }) => {
  // Memoizar children para evitar re-cálculo en cada render
  const children = React.useMemo(() => {
    return config.arrayItemConfig.children
      ?.filter((child: any) => item[child.key] !== undefined)
      .map((child: any) => ({
        ...child,
        value: item[child.key]
      }));
  }, [item, config.arrayItemConfig.children]);

  return (
    <div className="array-item">
      <div className="array-item-header">
        <span className="array-item-label">Item {index + 1}</span>
        <button
          type="button"
          className="btn btn-sm btn-danger"
          onClick={() => onRemove(index)}
        >
          <Trash2 size={12} />
        </button>
      </div>
      <div className="array-item-content">
        <ObjectField
          config={{
            ...config.arrayItemConfig,
            value: item,
            key: '',
            children
          }}
          onChange={onChange}
          path={`${fullPath}[${index}]`}
          imageService={imageService}
        />
      </div>
    </div>
  );
});

ArrayObjectItem.displayName = 'ArrayObjectItem';

export const ArrayField: React.FC<FieldComponentProps & { imageService?: ImageService }> = ({ 
  config, 
  onChange, 
  onAddArrayItem, 
  onRemoveArrayItem, 
  path,
  imageService
}) => {
  const fullPath = path ? `${path}.${config.key}` : config.key;
  const [isExpanded, setIsExpanded] = React.useState(true);
  
  const handleAddItem = () => {
    if (onAddArrayItem) {
      onAddArrayItem(fullPath);
    }
  };

  const handleRemoveItem = (index: number) => {
    if (onRemoveArrayItem) {
      onRemoveArrayItem(fullPath, index);
    }
  };

  return (
    <div className="field-group array-field">
      <div className="array-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="array-title">
          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <label className="field-label">
            {config.label}
            {config.description && <span className="field-description"> - {config.description}</span>}
          </label>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-primary"
          onClick={(e) => {
            e.stopPropagation();
            handleAddItem();
          }}
        >
          <Plus size={14} />
          Agregar
        </button>
      </div>
      
      {isExpanded && (
        <div className="array-items">
          {config.value && config.value.length > 0 ? (
            config.value.map((item: any, index: number) => (
              <React.Fragment key={index}>
                {config.arrayItemType === 'object' && config.arrayItemConfig ? (
                  <ArrayObjectItem
                    item={item}
                    index={index}
                    config={config}
                    fullPath={fullPath}
                    onChange={onChange}
                    onRemove={handleRemoveItem}
                    imageService={imageService}
                  />
                ) : (
                  <div className="array-item">
                    <div className="array-item-header">
                      <span className="array-item-label">Item {index + 1}</span>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => handleRemoveItem(index)}
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                    <div className="array-item-content">
                        {config.arrayItemType === 'string' ? (
                          <ArrayStringItem
                            value={item || ''}
                            index={index}
                            onChange={(newValue) => onChange(`${fullPath}[${index}]`, newValue)}
                            placeholder={`Item ${index + 1}`}
                          />
                        ) : (
                          <input
                            type={config.arrayItemType === 'number' ? 'number' : 'text'}
                            className="input"
                            value={item || ''}
                            onChange={(e) => {
                              const newValue = config.arrayItemType === 'number' 
                                ? parseFloat(e.target.value) || 0
                                : e.target.value;
                              onChange(`${fullPath}[${index}]`, newValue);
                            }}
                            placeholder={`Item ${index + 1}`}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              ))
          ) : (
            <div className="array-empty">
              <p>No hay elementos en esta lista</p>
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={handleAddItem}
              >
                <Plus size={14} />
                Agregar primer elemento
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const ObjectField: React.FC<FieldComponentProps & { imageService?: ImageService }> = ({ config, onChange, path, imageService }) => {
  const fullPath = config.key ? (path ? `${path}.${config.key}` : config.key) : path || '';
  const [isExpanded, setIsExpanded] = React.useState(true);
  
  return (
    <div className="field-group object-field">
      <div className="object-header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="object-title">
          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <label className="field-label">
            {config.label}
            {config.description && <span className="field-description"> - {config.description}</span>}
          </label>
        </div>
      </div>
      
      {isExpanded && config.children && (
        <div className="object-content">
          {config.children.map((child) => (
            <FieldRenderer
              key={child.key}
              config={child}
              onChange={onChange}
              path={fullPath}
              imageService={imageService}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface FieldRendererProps extends FieldComponentProps {
  imageService?: ImageService;
}

export const FieldRenderer: React.FC<FieldRendererProps> = ({ 
  config, 
  onChange, 
  onAddArrayItem, 
  onRemoveArrayItem, 
  path, 
  imageService 
}) => {
  switch (config.type) {
    case 'string':
      return <StringField config={config} onChange={onChange} path={path} />;
    case 'number':
      return <NumberField config={config} onChange={onChange} path={path} />;
    case 'boolean':
      return <BooleanField config={config} onChange={onChange} path={path} />;
    case 'image':
      return imageService ? (
        <ImageField 
          config={config} 
          onChange={onChange} 
          path={path} 
          imageService={imageService}
        />
      ) : (
        <StringField config={config} onChange={onChange} path={path} />
      );
    case 'array':
      return (
        <ArrayField
          config={config}
          onChange={onChange}
          onAddArrayItem={onAddArrayItem}
          onRemoveArrayItem={onRemoveArrayItem}
          path={path}
          imageService={imageService}
        />
      );
    case 'object':
      return <ObjectField config={config} onChange={onChange} path={path} imageService={imageService} />;
    default:
      return <StringField config={config} onChange={onChange} path={path} />;
  }
};
